import type { BoardCase } from "@/api/board/boardCaseFields";
import { useTheme } from "@/theme/ThemeProvider";
import { activateButtonOnKey } from "@/ui/a11y/buttonKey";
import {
  activeFilterLabel,
  caseAnimalLabel,
  caseBoardStatus,
  caseSiteLabel,
  caseSpeciesLabel,
  caseTableDensity,
  CASE_COLUMN_FLEX,
  CASE_COLUMN_MIN_WIDTH,
  CASE_TABLE_COLUMN_KEYS,
  nextTaskLines,
  type BoardKpiKey,
  distinctLabels,
  filterBoardCases,
  formatCaseWeight,
  phoneCaseDetail,
  pruneCaseFilter,
  sortBoardCases,
  speciesFilterOptions,
  type CaseTableDensity,
  type CaseTableFilter,
} from "@/ui/board/caseTableModel";
import { AppButton } from "@/ui/components/AppButton";
import { DataTable, type DataTableColumn } from "@/ui/components/DataTable";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import { StatusChip } from "@/ui/components/StatusChip";
import { useEffect, useMemo, useState } from "react";
import { Modal, Platform, Pressable, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const EMPTY_FILTER: CaseTableFilter = { species: [], site: null };

/**
 * Pflegefälle als Tabelle. Die Dichte folgt der gemessenen Containerbreite:
 * ab 900 sieben Spalten, ab dem reservierten Tablet-Bedarf die Tablet-Spalten
 * inklusive Art, darunter die Phone-Zeile. Vor der ersten Messung bleibt die Fläche leer.
 */
export function CaseTable({
  cases,
  width,
  now: nowProp,
  kpi = null,
  onKpiChange,
  onOpenCase,
  onOpenProtocol,
  onVisibleCount,
}: {
  cases: readonly BoardCase[];
  /** Gemockte Containerbreite. Sonst misst onLayout den Container. */
  width?: number;
  /** Gemeinsame Uhr mit den KPI-Kacheln. Ohne Prop tickt die Tabelle selbst. */
  now?: Date;
  /** KPI-Filter aus der Leiste. Wirkt zusätzlich zu Art und Pflegestelle. */
  kpi?: BoardKpiKey | null;
  onKpiChange?: (kpi: BoardKpiKey | null) => void;
  onOpenCase: (caseId: string) => void;
  onOpenProtocol: (caseId: string) => void;
  /** Gefilterte Anzahl für die Überschrift „N Fälle“. */
  onVisibleCount?: (count: number) => void;
}) {
  const { theme } = useTheme();
  const [measured, setMeasured] = useState<number | null>(null);
  const knownWidth = width ?? measured;
  const ready = knownWidth != null && knownWidth > 0;
  const density = caseTableDensity(knownWidth ?? 0);
  const [internalNow, setInternalNow] = useState(() => new Date());
  const now = nowProp ?? internalNow;
  const [filter, setFilter] = useState<CaseTableFilter>(EMPTY_FILTER);
  useEffect(() => {
    if (nowProp != null) return;
    const timer = setInterval(() => setInternalNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, [nowProp]);

  const speciesOptions = useMemo(() => speciesFilterOptions(cases), [cases]);
  const siteOptions = useMemo(
    () => distinctLabels(cases.map((row) => row.current_care_site_name)),
    [cases],
  );
  const visible = useMemo(
    () => sortBoardCases(filterBoardCases(cases, { ...filter, kpi }, now), now),
    [cases, filter, kpi, now],
  );
  useEffect(() => {
    onVisibleCount?.(visible.length);
  }, [onVisibleCount, visible.length]);
  const active = activeFilterLabel({ ...filter, kpi });
  useEffect(() => {
    setFilter((current) => pruneCaseFilter(current, cases));
  }, [cases]);
  const chipHeight = theme.control.heightTouch;
  const rowHeight = density === "phone" ? theme.table.rowCasePhone : density === "tablet" ? theme.table.rowTouch : theme.table.rowCase;

  const columns = useMemo(
    () =>
      density === "phone"
        ? []
        : columnsFor(density, {
            onOpenCase,
            onOpenProtocol,
            actionHeight: density === "desktop" ? undefined : theme.control.heightTouch,
            now,
          }),
    [density, onOpenCase, onOpenProtocol, theme.control.heightTouch, now],
  );

  return (
    <View
      testID="case-table-measure"
      onLayout={(event) => {
        if (width != null) return;
        const next = event.nativeEvent.layout.width;
        setMeasured((current) => (current === next ? current : next));
      }}
      style={{ width: "100%", maxWidth: "100%", gap: theme.space.sm }}
    >
      {ready ? (
      <>
      <View
        testID="case-filter-bar"
        style={{
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: theme.space.sm,
          width: "100%",
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <FilterChips
            label="Art"
            options={speciesOptions}
            selected={filter.species}
            minHeight={chipHeight}
            onToggle={(value) =>
              setFilter((current) => ({
                ...current,
                species: current.species.includes(value)
                  ? current.species.filter((item) => item !== value)
                  : [...current.species, value],
              }))
            }
          />
        </View>
        <SiteFilter
          density={density}
          options={siteOptions}
          value={filter.site}
          minHeight={Math.max(chipHeight, theme.control.heightTouch)}
          onChange={(site) => setFilter((current) => ({ ...current, site }))}
        />
        {active ? (
          <AppButton
            variant="ghost"
            accessibilityRole="button"
            accessibilityLabel="Filter zurücksetzen"
            onPress={() => {
              setFilter(EMPTY_FILTER);
              onKpiChange?.(null);
            }}
            style={{ minHeight: Math.max(chipHeight, theme.control.heightTouch), paddingVertical: 0 }}
          >
            Zurücksetzen
          </AppButton>
        ) : null}
      </View>
      {active ? (
        <Text
          style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "600" }}
        >
          {`Aktiv: ${active}`}
        </Text>
      ) : null}

      {cases.length === 0 ? (
        <EmptyState title="Keine zugänglichen Fälle in Pflege." />
      ) : visible.length === 0 ? (
        <EmptyState title="Keine Pflegefälle für diesen Filter." />
      ) : density === "phone" ? (
        <View
          accessibilityRole="list"
          accessibilityLabel="Pflegefälle"
          style={{ width: "100%", overflow: "hidden" }}
        >
          {visible.map((row) => (
            <View key={row.case_id} role="listitem">
              <PhoneCaseRow
                row={row}
                minHeight={rowHeight}
                now={now}
                onPress={() => onOpenCase(row.case_id)}
              />
            </View>
          ))}
        </View>
      ) : (
        <DataTable
          label="Pflegefälle"
          columns={columns}
          rows={visible}
          rowKey={(row) => row.case_id}
          rowHeight={rowHeight}
          style={{ width: "100%" }}
        />
      )}
      </>
      ) : null}
    </View>
  );
}

export function caseTableColumnKeys(width: number): readonly string[] {
  return CASE_TABLE_COLUMN_KEYS[caseTableDensity(width)];
}

function columnsFor(
  density: Exclude<CaseTableDensity, "phone">,
  actions: {
    onOpenCase: (caseId: string) => void;
    onOpenProtocol: (caseId: string) => void;
    actionHeight: number | undefined;
    now: Date;
  },
): DataTableColumn<BoardCase>[] {
  const tier: DataTableColumn<BoardCase> = {
    key: "tier",
    header: "Tier",
    render: (row) => caseAnimalLabel(row),
  };
  const weight: DataTableColumn<BoardCase> = {
    key: "gewicht",
    header: "Gewicht",
    render: (row) => formatCaseWeight(row),
  };
  const task: DataTableColumn<BoardCase> = {
    key: "aufgabe",
    header: "Nächste Aufgabe",
    render: (row) => <NextTaskCell row={row} now={actions.now} />,
  };
  const art: DataTableColumn<BoardCase> = {
    key: "art",
    header: "Art",
    render: (row) => caseSpeciesLabel(row),
  };
  const state: DataTableColumn<BoardCase> = {
    key: "status",
    header: "Status",
    render: (row) => <CaseStatus row={row} now={actions.now} />,
  };
  const akte: DataTableColumn<BoardCase> = {
    key: "akte",
    header: "Akte",
    render: (row) => (
      <CaseActions
        row={row}
        protocol={false}
        minHeight={actions.actionHeight}
        onOpenCase={actions.onOpenCase}
        onOpenProtocol={actions.onOpenProtocol}
      />
    ),
  };
  if (density === "tablet") {
    return [tier, art, weight, task, state, akte].map(sizedColumn);
  }
  return [
    tier,
    art,
    weight,
    { key: "pflegestelle", header: "Pflegestelle", render: (row) => caseSiteLabel(row) },
    task,
    state,
    {
      key: "aktionen",
      header: "Aktionen",
      render: (row) => (
        <CaseActions
          row={row}
          protocol
          minHeight={actions.actionHeight}
          onOpenCase={actions.onOpenCase}
          onOpenProtocol={actions.onOpenProtocol}
        />
      ),
    },
  ].map(sizedColumn);
}

const WRAPPED_COLUMNS = new Set(["art", "gewicht", "aufgabe", "pflegestelle"]);

function sizedColumn(column: DataTableColumn<BoardCase>): DataTableColumn<BoardCase> {
  const fixed = column.key === "status" || column.key === "aktionen" || column.key === "akte";
  const minWidth = CASE_COLUMN_MIN_WIDTH[column.key];
  return {
    ...column,
    minWidth,
    lines: WRAPPED_COLUMNS.has(column.key) ? 2 : column.lines,
    width: fixed ? minWidth : column.width,
    flex: fixed ? undefined : CASE_COLUMN_FLEX[column.key] ?? 1,
    flexShrink: fixed ? 0 : 1,
  };
}

function NextTaskCell({ row, now }: { row: BoardCase; now: Date }) {
  const { theme } = useTheme();
  const lines = nextTaskLines(row, now);
  const text = {
    color: theme.color.text,
    fontSize: theme.fontSize.small,
    fontWeight: "600" as const,
  };
  if (!lines || (!lines.label && !lines.when)) {
    return <Text style={text}>–</Text>;
  }
  if (!lines.when) {
    return (
      <Text style={text} numberOfLines={2}>
        {lines.label}
      </Text>
    );
  }
  if (!lines.label) {
    return (
      <Text style={text} numberOfLines={1}>
        {lines.when}
      </Text>
    );
  }
  return (
    <View>
      <Text style={text} numberOfLines={2}>
        {lines.label}
      </Text>
      <Text style={text} numberOfLines={1}>
        {lines.when}
      </Text>
    </View>
  );
}

function CaseStatus({ row, now }: { row: BoardCase; now: Date }) {
  const { theme } = useTheme();
  const state = caseBoardStatus(row, now);
  if (!state.tone) {
    return (
      <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "600" }}>
        {state.label}
      </Text>
    );
  }
  return <StatusChip label={state.label} tone={state.tone} size="sm" />;
}

function CaseActions({
  row,
  protocol,
  minHeight,
  onOpenCase,
  onOpenProtocol,
}: {
  row: BoardCase;
  protocol: boolean;
  minHeight: number | undefined;
  onOpenCase: (caseId: string) => void;
  onOpenProtocol: (caseId: string) => void;
}) {
  const { theme } = useTheme();
  const animal = caseAnimalLabel(row);
  const buttonStyle = { minHeight, paddingVertical: 0, paddingHorizontal: theme.space.xs };
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: theme.space.xs, minWidth: 0 }}>
      {protocol ? (
        <AppButton
          variant="ghost"
          accessibilityRole="button"
          accessibilityLabel={`Protokoll ${animal}`}
          onPress={() => onOpenProtocol(row.case_id)}
          style={buttonStyle}
          labelStyle={{ fontSize: theme.fontSize.small, lineHeight: 18 }}
        >
          Protokoll
        </AppButton>
      ) : null}
      <AppButton
        variant="ghost"
        accessibilityRole="button"
        accessibilityLabel={`Akte ${animal}`}
        onPress={() => onOpenCase(row.case_id)}
        style={buttonStyle}
        labelStyle={{ fontSize: theme.fontSize.small, lineHeight: 18 }}
      >
        Akte
      </AppButton>
    </View>
  );
}

function PhoneCaseRow({
  row,
  minHeight,
  now,
  onPress,
}: {
  row: BoardCase;
  minHeight: number;
  now: Date;
  onPress: () => void;
}) {
  const { theme } = useTheme();
  const animal = caseAnimalLabel(row);
  const stateLabel = caseBoardStatus(row, now).label;
  const detail = phoneCaseDetail(row, now);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={detail ? `${animal}, ${stateLabel}. ${detail}` : `${animal}, ${stateLabel}.`}
      onPress={onPress}
      {...{
        onKeyDown: (event: { key?: string; nativeEvent?: { key?: string }; preventDefault?: () => void }) =>
          activateButtonOnKey(event, onPress),
      }}
      style={{
        minHeight,
        width: "100%",
        justifyContent: "center",
        gap: 2,
        borderBottomWidth: 1,
        borderBottomColor: theme.color.border,
        paddingHorizontal: theme.space.sm,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: theme.space.sm, minWidth: 0 }}>
        <Text
          style={{
            flex: 1,
            minWidth: 0,
            color: theme.color.text,
            fontSize: theme.fontSize.small,
            fontWeight: "700",
          }}
          numberOfLines={1}
        >
          {animal}
        </Text>
        <CaseStatus row={row} now={now} />
      </View>
      {detail ? (
        <Text
          style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "600" }}
          numberOfLines={1}
        >
          {detail}
        </Text>
      ) : null}
    </Pressable>
  );
}

function FilterChips({
  label,
  options,
  selected,
  minHeight,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: readonly string[];
  minHeight: number;
  onToggle: (value: string) => void;
}) {
  const { theme } = useTheme();
  if (options.length === 0) return null;
  const chosen = new Set(selected);
  return (
    <View
      testID="species-filter-chips"
      accessibilityLabel={label}
      role="group"
      style={{
        flex: 1,
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: theme.space.dense,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Text
        style={{
          color: theme.color.muted,
          fontSize: theme.fontSize.small,
          fontWeight: "700",
          letterSpacing: 0.35,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Text>
      {options.map((option) => {
        const on = chosen.has(option);
        return (
          <Pressable
            key={option}
            accessibilityRole="button"
            accessibilityLabel={`${label} ${option}`}
            {...(on ? { "aria-pressed": true as const } : { "aria-pressed": false as const })}
            onPress={() => onToggle(option)}
            style={{
              minHeight,
              justifyContent: "center",
              paddingHorizontal: theme.space.sm,
              borderRadius: theme.radius.control,
              borderWidth: on ? 2 : 1,
              borderColor: on ? theme.color.primary : theme.color.inputBorder,
              backgroundColor: on ? theme.color.accentBg : theme.color.surface,
            }}
          >
            <Text
              style={{
                color: theme.color.text,
                fontSize: theme.fontSize.label,
                fontWeight: "600",
              }}
            >
              {option}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function SiteFilter({
  density,
  options,
  value,
  minHeight,
  onChange,
}: {
  density: CaseTableDensity;
  options: readonly string[];
  value: string | null;
  minHeight: number;
  onChange: (site: string | null) => void;
}) {
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const [open, setOpen] = useState(false);
  if (options.length === 0) return null;
  const choices = [{ value: null as string | null, label: "Alle" }, ...options.map((option) => ({ value: option, label: option }))];
  if (density !== "phone") {
    return (
      <View style={{ width: 200, flexShrink: 0, minHeight }}>
        <FloatingLabelListPicker
          label="Pflegestelle"
          options={choices}
          value={value}
          onChange={onChange}
          placeholder="Alle"
        />
      </View>
    );
  }
  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={value ? `Pflegestelle ${value}` : "Pflegestelle Alle"}
        onPress={() => setOpen(true)}
        style={{
          minHeight,
          flexShrink: 0,
          justifyContent: "center",
          paddingHorizontal: theme.space.sm,
          borderRadius: theme.radius.control,
          borderWidth: 1,
          borderColor: theme.color.inputBorder,
          backgroundColor: theme.color.surface,
        }}
      >
        <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "700" }}>
          Pflegestelle
        </Text>
        <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small, fontWeight: "600" }}>
          {value ?? "Alle"}
        </Text>
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <View style={{ flex: 1, justifyContent: "flex-end" }}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Filter schließen"
            onPress={() => setOpen(false)}
            style={{ flex: 1 }}
          />
          <View
            testID="site-filter-sheet"
            style={{
              backgroundColor: theme.color.surface,
              borderTopLeftRadius: theme.radius.panel,
              borderTopRightRadius: theme.radius.panel,
              padding: theme.space.md,
              paddingBottom: theme.space.md + insets.bottom,
              gap: theme.space.xs,
            }}
          >
            <Text style={{ color: theme.color.text, fontWeight: "700" }}>Pflegestelle</Text>
            {choices.map((choice) => (
              <Pressable
                key={choice.label}
                accessibilityRole="radio"
                accessibilityState={{ checked: value === choice.value }}
                {...(Platform.OS === "web"
                  ? { "aria-checked": value === choice.value }
                  : {})}
                accessibilityLabel={`Pflegestelle ${choice.label}`}
                onPress={() => {
                  onChange(choice.value);
                  setOpen(false);
                }}
                style={{ minHeight, justifyContent: "center" }}
              >
                <Text style={{ color: theme.color.text, fontWeight: value === choice.value ? "700" : "600" }}>
                  {choice.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      </Modal>
    </>
  );
}
